import axios, { AxiosResponse } from 'axios'
import { normalizeCurrencyCode } from '@/lib/utils'

export interface ExchangeRateResponse {
  result: string
  documentation: string
  terms_of_use: string
  time_last_update_unix: number
  time_last_update_utc: string
  time_next_update_unix: number
  time_next_update_utc: string
  base_code: string
  target_code: string
  conversion_rate: number
  conversion_result?: number
}

class ExchangeRateClient {
  private apiKey: string
  private baseURL = 'https://v6.exchangerate-api.com/v6'

  constructor() {
    this.apiKey = import.meta.env.VITE_API_ExchangeRate || '57e929b0c2aa63bd8cdc9519'
    
    if (!this.apiKey) {
      console.warn('ExchangeRate API key not found in environment variables')
    }
  }

  /**
   * Get exchange rate between two currencies
   * @param fromCurrency - Base currency (e.g., 'EUR')
   * @param toCurrency - Target currency (e.g., 'GBP')
   * @returns Promise with exchange rate data
   */
  async getExchangeRate(fromCurrency: string, toCurrency: string): Promise<AxiosResponse<ExchangeRateResponse>> {
    if (!this.apiKey) {
      throw new Error('ExchangeRate API key not configured')
    }

    const url = `${this.baseURL}/${this.apiKey}/pair/${fromCurrency}/${toCurrency}`
    
    return axios.get(url, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }

  /**
   * Convert amount from one currency to another
   * @param fromCurrency - Base currency (e.g., 'EUR')
   * @param toCurrency - Target currency (e.g., 'GBP')
   * @param amount - Amount to convert
   * @returns Promise with conversion result
   */
  async convertCurrency(fromCurrency: string, toCurrency: string, amount: number): Promise<AxiosResponse<ExchangeRateResponse>> {
    if (!this.apiKey) {
      throw new Error('ExchangeRate API key not configured')
    }

    const url = `${this.baseURL}/${this.apiKey}/pair/${fromCurrency}/${toCurrency}/${amount}`
    
    return axios.get(url, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }

  /**
   * Get exchange rate for user's currency to EUR (for display purposes)
   * @param userCurrency - User's wallet currency
   * @returns Promise with exchange rate data
   */
  async getUserCurrencyToEUR(userCurrency: string): Promise<AxiosResponse<ExchangeRateResponse>> {
    // If user currency is already EUR, return 1:1 rate
    if (userCurrency === 'EUR') {
      return Promise.resolve({
        data: {
          result: 'success',
          documentation: '',
          terms_of_use: '',
          time_last_update_unix: Date.now() / 1000,
          time_last_update_utc: new Date().toUTCString(),
          time_next_update_unix: Date.now() / 1000 + 3600,
          time_next_update_utc: new Date(Date.now() + 3600000).toUTCString(),
          base_code: 'EUR',
          target_code: 'EUR',
          conversion_rate: 1.0
        },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {} as any
      })
    }

    const apiCurrency = normalizeCurrencyCode(userCurrency)
    return this.getExchangeRate(apiCurrency, 'EUR')
  }
}

export const exchangeRateApi = new ExchangeRateClient()
